// `/l/$slug`: resolves the slug against the locations of the session's account, switches the session to that location
// when it acts elsewhere (its permissions are the ones held there), then renders the shell with the active location.
// An unknown slug goes to the open location's dashboard (legacy's default location, routes.md §2 step 6).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useMatches, useNavigate, useParams } from '@tanstack/react-router';
import { LoadingState, useToast } from '@radish/ui-web';
import { clearLostScope, currentSession, locationsOf, loginSearch, lostScopeSnapshot, switchTo, type LostScope } from '../session/session.ts';
import { staffMenuOf } from '../shell/StaffShell.tsx';
import { useSignedIn } from '../session/use-session.ts';
import { AppShell } from '../shell/AppShell.tsx';
import { LoadError } from '../shell/page.tsx';
import { pathFor, SCREENS, type AppPath } from '../router-paths.ts';
import { activeLocationOf, ActiveLocationProvider } from './location.tsx';
import { mayOpen } from './page-permissions.ts';

const isAppPath = (path: string): path is AppPath => path in SCREENS;

function useCurrentPage(): { readonly title: string; readonly path: AppPath | null } {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  const route = leaf?.fullPath.replace(/\/$/, '') ?? '';
  const titled = [...matches].reverse().find((m) => typeof m.staticData.title === 'string');
  return { title: titled?.staticData.title ?? 'Dashboard', path: isAppPath(route) ? route : null };
}

export function LocationLayout(): ReactNode {
  const { slug } = useParams({ strict: false });
  useSignedIn();
  const session = currentSession();
  const page = useCurrentPage();
  const all = session === null ? [] : locationsOf(session.view);
  const location = all.find((l) => l.slug === slug);
  const [problem, setProblem] = useState<string | null>(null);
  const needsSwitch = session !== null && location !== undefined && session.view.current.locationId !== location.locationId;

  useEffect(() => {
    if (location !== undefined) document.title = `${page.title} · ${location.name} · Radish`;
  }, [location, page.title]);

  const accountId = session?.view.current.accountId;
  const target = needsSwitch ? location.locationId : null;
  useEffect(() => {
    if (target === null || accountId === undefined) return;
    let live = true;
    void switchTo(accountId, target).then((r) => {
      if (live && !r.ok) setProblem(r.message);
    });
    return () => {
      live = false;
    };
  }, [accountId, target]);

  if (session === null) return <Navigate to="/login" search={loginSearch()} />;
  // A192: this tab's location was lost at a token refresh. Never a silent move: say so and open the picker.
  const lost = lostScopeSnapshot();
  // Arrived at another location (the picker's): the lost scope has been said, and this page is the tab's own again.
  if (lost !== null && location !== undefined && location.locationId !== lost.locationId) clearLostScope();
  else if (lost !== null) {
    const staff = staffMenuOf(session.view) !== null;
    const fallback = all.find((l) => l.locationId === session.view.current.locationId) ?? all[0];
    return <ScopeLost lost={lost} to={staff || fallback === undefined ? null : fallback.slug} />;
  }
  if (location === undefined) {
    // Legacy sends an unknown slug to the default location, the one last opened: the one the session acts in.
    const fallback = all.find((l) => l.locationId === session.view.current.locationId) ?? all[0];
    return fallback === undefined ? <LoadError error={null} /> : <Navigate to={pathFor('/l/$slug', fallback.slug)} />;
  }
  if (problem !== null) return <LoadError error={null} />;
  if (needsSwitch) return <LoadingState label="Loading location" />;
  const active = activeLocationOf(location, all, session);
  if (!mayOpen(page.path, active.can)) return <Refused slug={location.slug} />;
  return (
    <ActiveLocationProvider value={active}>
      <AppShell title={page.title} path={page.path}>
        <div data-screen={page.path === null ? undefined : SCREENS[page.path]}>
          <Outlet />
        </div>
      </AppShell>
    </ActiveLocationProvider>
  );
}

/**
 * Legacy's router guard: "You do not have permission to access this page." and the location's Access Denied page, in
 * place of the refused one. Navigates once (a `<Navigate>` re-navigates on every render, and the toast re-renders).
 */
function Refused({ slug }: { readonly slug: string }): ReactNode {
  const toast = useToast();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    toast.error('You do not have permission to access this page.');
    void navigate({ to: pathFor('/l/$slug/access-denied', slug), replace: true });
  }, [toast, navigate, slug]);
  return null;
}

/**
 * A192 (Andrew, 2026-10-23): a tab whose location this person may no longer act at goes to the location picker with a
 * toast, never silently elsewhere: Radish staff and resellers to Accounts, a merchant to the location switcher opened over
 * the location the session now acts at. Navigates once (the toast re-renders).
 */
function ScopeLost({ lost, to }: { readonly lost: LostScope; readonly to: string | null }): ReactNode {
  const toast = useToast();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    toast.error(`You no longer have access to ${lost.name}.`);
    // A merchant's tab clears it on arriving at the picker's location (the layout above); Accounts has no layout to.
    if (to === null) void navigate({ to: '/accounts', replace: true }).then(clearLostScope);
    else void navigate({ to: pathFor('/l/$slug', to) as never, search: { pick: 1 } as never, replace: true });
  }, [toast, navigate, lost, to]);
  return <LoadingState label="Choose a location" />;
}
